import React, { useEffect, useMemo } from 'react';
import Sortable from 'sortablejs';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import './Table.scss';
const rootClassName = 'component-table';

type indexes = string | number;

type AceptedValue = string | React.ReactNode | number;

export interface TableCell extends React.HTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right' | 'justify' | 'char';
}

export type ColumnObject<R extends indexes | undefined = undefined> = {
  key: R extends indexes ? R : indexes;
  value: AceptedValue;
  props?: TableCell;
  // sticky?: boolean;
};

export type LineObject<R extends indexes | undefined = undefined> =
  R extends indexes ? { [key in R]: AceptedValue } : any;

interface BasicTableProps {
  columns: unknown[];
  lines?: unknown[];
  className?: string;
  sortable?: boolean;
  onSort?: (event?: { oldIndex: number; newIndex: number }) => void;
  confirmSort?: (event?: any) => Promise<any>;
  upperHeader?: {
    active?: boolean;
    buttons?: {
      id?: string;
      disabled?: boolean;
      icon?: React.ReactNode;
      label?: string;
      onClick?: () => void;
    }[];
  };
}
export interface TableProps extends BasicTableProps {
  columns: ColumnObject[] | ColumnObject<indexes>[];
  lines?: LineObject[] | LineObject<indexes>[];
}

export interface TypedTableProps<R extends indexes> extends BasicTableProps {
  columns: ColumnObject<R>[];
  lines?: LineObject<R>[];
}

export function TypedTable<R extends indexes>(props: TypedTableProps<R>) {
  return <Table {...props} />;
}

export default function Table({
  columns,
  lines,
  onSort,
  confirmSort,
  sortable,
  upperHeader,
  className,
  ...props
}: TableProps): JSX.Element {
  const itemsId = useMemo(() => `table${getUniqueKey()}`, []);

  useEffect(() => {
    let sortableComponent: Sortable;

    if (sortable) {
      const el = document.getElementById(itemsId) as HTMLElement;
      sortableComponent = new Sortable(el, {
        animation: 300,
        ghostClass: `${rootClassName}-item-ghost`,
        dragClass: `${rootClassName}-item-dragging`,
        onStart: () => {
          document.body.classList.add('document-grabbing');
        },
        onEnd: (evt: any) => {
          document.body.classList.remove('document-grabbing');
          if (evt.oldIndex !== evt.newIndex) {
            const confirmFirst = confirmSort ?? (() => Promise.resolve());
            confirmFirst?.(evt)
              .then(() => {
                onSort?.({
                  oldIndex: evt.oldIndex - 1,
                  newIndex: evt.newIndex - 1,
                });
              })
              ?.catch(() => {
                const dLine = document.querySelector(
                  `#${itemsId} > :nth-child(${evt.newIndex + 1})`
                ) as HTMLElement;
                const postLine = document.querySelector(
                  `#${itemsId} > :nth-child(${
                    evt.newIndex > evt.oldIndex
                      ? evt.oldIndex + 1
                      : evt.oldIndex + 2
                  })`
                ) as HTMLElement;
                el.insertBefore(dLine, postLine);
              });
          }
        },
        handle: '.grab-handle',
      });
    }

    return () => {
      sortableComponent?.destroy();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortable, itemsId]);

  return (
    <div className={`${rootClassName}-outer`}>
      {upperHeader ? (
        <React.Fragment>
          <div
            className={getMergedClassNames([
              `${rootClassName}-upper-header`,
              upperHeader.active ? 'active' : '',
            ])}
          >
            {upperHeader.buttons && upperHeader.buttons.length
              ? upperHeader.buttons.map((btn, index) => {
                  return (
                    <button
                      id={btn.id || undefined}
                      key={index}
                      onClick={btn.onClick}
                      disabled={!upperHeader?.active || btn?.disabled || false}
                    >
                      <span>{btn.icon}</span>
                      {btn.label}
                    </button>
                  );
                })
              : undefined}
          </div>
        </React.Fragment>
      ) : undefined}
      <div className={`${rootClassName}-outer-table`}>
        <table
          {...props}
          className={getMergedClassNames([
            rootClassName,
            className || '',
            upperHeader ? 'has-upper-header' : '',
          ])}
        >
          <thead>
            <tr>
              <th />
              {columns && columns.length
                ? Array.from(columns as Array<ColumnObject<any>>).map(
                    (column: ColumnObject<any>, columnIndex) => {
                      return (
                        <th
                          {...column.props}
                          key={column.key ? column.key : columnIndex}
                        >
                          {column.value}
                        </th>
                      );
                    }
                  )
                : undefined}
              <th />
            </tr>
          </thead>
          <tbody id={itemsId}>
            <tr />
            {lines && lines.length
              ? Array.from(lines as Array<LineObject<any>>).map(
                  (line: any, index) => {
                    const key = line.key ? line.key : index;
                    delete line.key;
                    const disabled = line.disabled || false;
                    delete line.disabled;
                    const lineProps = line.props ? line.props : {};
                    delete line.props;
                    return (
                      <tr key={key} aria-disabled={disabled} {...lineProps}>
                        <td />
                        {Object.values(line).map((cell: any, valueIndex) => {
                          const columnProps = columns[valueIndex]?.props ?? {};
                          return (
                            <td
                              key={cell && cell.key ? cell.key : valueIndex}
                              {...columnProps}
                              {...(cell &&
                              cell.props &&
                              !React.isValidElement(cell)
                                ? cell.props
                                : {})}
                            >
                              {cell}
                            </td>
                          );
                        })}
                        <td />
                      </tr>
                    );
                  }
                )
              : undefined}
          </tbody>
        </table>
      </div>
    </div>
  );
}
